import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { map } from 'rxjs';
import { environment } from '../../environments/environment';
import { MessageChannel } from './communication-config';

/**
 * The text per channel + event — the WHAT of a message. Same resolution rule as
 * the provider config: a partner row (partnerId = X) overrides the global
 * default (partnerId = null) for the same templateKey + channel. Only active
 * templates are resolved at send time.
 */
export interface MessageTemplateDto {
  id: number;
  partnerId: number | null; // null => not partner-scoped
  clientId: number | null;  // null => not client-scoped; both null => Global default
  channel: MessageChannel;
  templateKey: string; // e.g. OTP_VERIFICATION
  subject?: string | null; // EMAIL only
  body: string; // supports {{Otp}} {{Name}} {{Minutes}}
  providerTemplateId?: string | null; // DLT (SMS) / approved WhatsApp template id
  /** WhatsApp: the language the template is registered in ('en', 'en_US'). Meta treats
   *  name + language as the template's identity — a mismatch fails as "template not found". */
  providerTemplateLang?: string | null;
  /** WhatsApp: registered base of a DYNAMIC URL button, e.g. 'https://rzp.io/i/{{1}}'.
   *  The send supplies only the piece after this base ({{Link}} minus the base) — never a
   *  whole URL. Blank = the template has no dynamic button. */
  providerButtonUrlBase?: string | null;
  isActive: boolean;
}

/** List/detail shape — adds the joined partner name when the backend supplies it. */
export interface MessageTemplate extends MessageTemplateDto {
  partnerName?: string | null;
}

/** Placeholders understood by the renderer; used by the "unknown placeholder" warning.
 *  {{Company}} is the dynamic brand name (resolved per client/partner, else the platform default). */
export const TEMPLATE_PLACEHOLDERS = [
  'Company', 'Name', 'Otp', 'Minutes', 'Amount', 'Link', 'PolicyNo', 'Receipt', 'Reason',
] as const;

/** Responses may be PascalCase; normalize to the camelCase model. */
function mapRow(x: any): MessageTemplate {
  return {
    id: x.Id ?? x.id ?? 0,
    partnerId: x.PartnerId ?? x.partnerId ?? null,
    clientId: x.ClientId ?? x.clientId ?? null,
    partnerName: x.PartnerName ?? x.partnerName ?? null,
    channel: (x.Channel ?? x.channel) as MessageChannel,
    templateKey: x.TemplateKey ?? x.templateKey ?? '',
    subject: x.Subject ?? x.subject ?? null,
    body: x.Body ?? x.body ?? '',
    providerTemplateId: x.ProviderTemplateId ?? x.providerTemplateId ?? null,
    providerTemplateLang: x.ProviderTemplateLang ?? x.providerTemplateLang ?? null,
    providerButtonUrlBase: x.ProviderButtonUrlBase ?? x.providerButtonUrlBase ?? null,
    isActive: (x.IsActive ?? x.isActive ?? true) === true,
  };
}

function mapEnvelope(res: any) {
  if (Array.isArray(res?.data)) return { ...res, data: res.data.map(mapRow) };
  if (res?.data && typeof res.data === 'object') return { ...res, data: mapRow(res.data) };
  if (Array.isArray(res)) return res.map(mapRow);
  return res;
}

@Injectable({ providedIn: 'root' })
export class MessageTemplateService {
  private http = inject(HttpClient);
  private readonly baseUrl = `${environment.apiUrl}/MessageTemplate`;

  /** List templates; `templateKey` and `channel` are optional filters. */
  getAll(templateKey?: string | null, channel?: MessageChannel | null) {
    const params: Record<string, string> = {};
    if (templateKey) params['templateKey'] = templateKey;
    if (channel) params['channel'] = channel;
    return this.http
      .get(`${this.baseUrl}/GetAll`, Object.keys(params).length ? { params } : {})
      .pipe(map(mapEnvelope));
  }

  getById(id: number) {
    return this.http.get(`${this.baseUrl}/GetById`, { params: { id } }).pipe(map(mapEnvelope));
  }

  /** Create (id 0) or update (id > 0). */
  upsert(dto: MessageTemplateDto) {
    return this.http.post(`${this.baseUrl}/Upsert`, dto);
  }

  /** Inline active toggle — doesn't round-trip the whole form. */
  setActive(id: number, isActive: boolean) {
    return this.http.post(`${this.baseUrl}/SetActive`, null, { params: { id, isActive } });
  }

  /** Hard delete (no undo). */
  delete(id: number) {
    return this.http.get(`${this.baseUrl}/Delete`, { params: { id } });
  }
}
